import React, { useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { CollateralBasketDetail, CrudEventEnum, EntityTypeEnum, CollateralBasketStatus, CrudActions, WorkflowActions } from 'api'
import { useModal } from 'view/shared/modal'
import { ActionsButtonProps } from 'view/components/datagrid'
import { SplitButtonDropdown, ActionItem } from 'view/components/menus'
import { CollateralBasketDialog } from './CollateralBasketDialog'
import { NotificationActions } from 'store/notification'
import { NotificationType } from 'store/notification/notificationTypes'
import { Translation, Locale } from 'locale'
import { ViewCollateralBasketDialog } from './CollateralBasketDialog/ViewCollateralBasketDialog'

interface AdminCollateralBasketActionsButtonProps extends ActionsButtonProps<CollateralBasketDetail> {}

const canViewDetails = (item: CollateralBasketDetail) =>
  item.collateralBasket.status !== CollateralBasketStatus.DELETED && !item.availableEvents.includes(CrudEventEnum.UPSERT)
const canEditDetails = (item: CollateralBasketDetail) =>
  item.collateralBasket.status !== CollateralBasketStatus.DELETED && item.availableEvents.includes(CrudEventEnum.UPSERT)
const canSuspendDetails = (item: CollateralBasketDetail) =>
  item.collateralBasket.status === CollateralBasketStatus.ACTIVE && item.availableEvents.includes(CrudEventEnum.UPSERT)
const canActivateDetails = (item: CollateralBasketDetail) =>
  item.collateralBasket.status === CollateralBasketStatus.SUSPENDED && item.availableEvents.includes(CrudEventEnum.UPSERT)
const canDeleteDetails = (item: CollateralBasketDetail) =>
  item.collateralBasket.status !== CollateralBasketStatus.DELETED && item.availableEvents.includes(CrudEventEnum.DELETE)

export const AdminCollateralBasketActionsButton: React.FC<AdminCollateralBasketActionsButtonProps> = ({ items }) => {
  const dispatch = useDispatch()
  const { showModal } = useModal()

  const viewItems = useMemo(() => items.filter(item => canViewDetails(item)), [items])
  const editItems = useMemo(() => items.filter(item => canEditDetails(item)), [items])
  const suspendItems = useMemo(() => items.filter(item => canSuspendDetails(item)), [items])
  const activateItems = useMemo(() => items.filter(item => canActivateDetails(item)), [items])
  const deleteItems = useMemo(() => items.filter(item => canDeleteDetails(item)), [items])

  const handleShowItemDialog = useCallback(
    (entityId: string) => {
      const correlatedResponseCallback = (workflowId: string) => {
        showModal(<CollateralBasketDialog workflowId={workflowId} entityId={entityId} />)
      }

      dispatch(WorkflowActions.createEditWorkflow(EntityTypeEnum.COLLATERAL_BASKET, entityId, correlatedResponseCallback))
    },
    [dispatch, showModal]
  )

  const handleViewItemDialog = useCallback(
    (entityId: string) => {
      showModal(<ViewCollateralBasketDialog entityId={entityId} />)
    },
    [showModal]
  )

  const handleSuspend = useCallback(
    (suspendCollateralBaskets: CollateralBasketDetail[]) => {
      const titleText = Locale.translate(Translation.TC_PROMPT_TITLE_SUSPEND_TRADING)

      const messageText =
        suspendCollateralBaskets.length === 1
          ? Locale.translate(Translation.SC_PROMPT_MSG_SUSPEND_SINGLE_COLLATERAL_BASKET, {
              name: suspendCollateralBaskets[0].collateralBasket.name
            })
          : Locale.translate(Translation.SC_PROMPT_MSG_SUSPEND_MULTI_COLLATERAL_BASKET, { count: suspendCollateralBaskets.length })

      const suspendActions = suspendCollateralBaskets.map(suspendItem =>
        CrudActions.collateralBasketUpsert({
          ...suspendItem.collateralBasket,
          status: CollateralBasketStatus.SUSPENDED
        })
      )

      dispatch(
        NotificationActions.showDialog({
          type: NotificationType.Confirm,
          titleText,
          messageText,
          buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
          defaultButton: Translation.TC_BUTTON_YES,
          cancelButton: Translation.TC_BUTTON_NO,
          responseMap: {
            [Translation.TC_BUTTON_YES]: suspendActions
          }
        })
      )
    },
    [dispatch]
  )

  const handleActivate = useCallback(
    (activateCollateralBaskets: CollateralBasketDetail[]) => {
      const titleText = Locale.translate(Translation.TC_PROMPT_TITLE_ACTIVATE_TRADING)

      const messageText =
        activateCollateralBaskets.length === 1
          ? Locale.translate(Translation.SC_PROMPT_MSG_ACTIVATE_SINGLE_COLLATERAL_BASKET, {
              name: activateCollateralBaskets[0].collateralBasket.name
            })
          : Locale.translate(Translation.SC_PROMPT_MSG_ACTIVATE_MULTI_COLLATERAL_BASKET, { count: activateCollateralBaskets.length })

      const activateActions = activateCollateralBaskets.map(activateItem =>
        CrudActions.collateralBasketUpsert({
          ...activateItem.collateralBasket,
          status: CollateralBasketStatus.ACTIVE
        })
      )

      dispatch(
        NotificationActions.showDialog({
          type: NotificationType.Confirm,
          titleText,
          messageText,
          buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
          defaultButton: Translation.TC_BUTTON_YES,
          cancelButton: Translation.TC_BUTTON_NO,
          responseMap: {
            [Translation.TC_BUTTON_YES]: activateActions
          }
        })
      )
    },
    [dispatch]
  )

  const handleDelete = useCallback(
    (deleteCollateralBaskets: CollateralBasketDetail[]) => {
      const titleText = Locale.translate(Translation.TC_PROMPT_TITLE_DELETE_COLLATERAL_BASKET, { count: deleteCollateralBaskets.length })

      const messageText =
        deleteCollateralBaskets.length === 1
          ? Locale.translate(Translation.SC_PROMPT_MSG_DELETE_SINGLE_COLLATERAL_BASKET, {
              name: deleteCollateralBaskets[0].collateralBasket.name
            })
          : Locale.translate(Translation.SC_PROMPT_MSG_DELETE_MULTI_COLLATERAL_BASKET, { count: deleteCollateralBaskets.length })

      const deleteActions = deleteCollateralBaskets.map(deleteItem =>
        CrudActions.collateralBasketUpsert({
          ...deleteItem.collateralBasket,
          status: CollateralBasketStatus.DELETED
        })
      )

      dispatch(
        NotificationActions.showDialog({
          type: NotificationType.Confirm,
          titleText,
          messageText,
          buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
          defaultButton: Translation.TC_BUTTON_YES,
          cancelButton: Translation.TC_BUTTON_NO,
          responseMap: {
            [Translation.TC_BUTTON_YES]: deleteActions
          }
        })
      )
    },
    [dispatch]
  )

  const actions: ActionItem[] = [
    {
      label: Locale.translate(Translation.TC_ACTION_VIEW_DETAILS),
      onClick: () => handleViewItemDialog(items[0].collateralBasket.id),
      enabled: viewItems.length === 1
    },
    {
      label: Locale.translate(Translation.TC_ACTION_EDIT_DETAILS),
      onClick: () => handleShowItemDialog(items[0].collateralBasket.id),
      enabled: editItems.length === 1
    },
    {
      label: Locale.translate(Translation.TC_ACTION_SUSPEND),
      onClick: () => handleSuspend(suspendItems),
      enabled: suspendItems.length > 0
    },
    {
      label: Locale.translate(Translation.TC_ACTION_ACTIVATE),
      onClick: () => handleActivate(activateItems),
      enabled: activateItems.length > 0
    },
    {
      label: Locale.translate(Translation.TC_ACTION_DELETE),
      onClick: () => handleDelete(deleteItems),
      enabled: deleteItems.length > 0
    }
  ]
  return <SplitButtonDropdown actions={actions} maxButtons={1} />
}
